import { redactSensitiveRecord } from './authLogin';
import { isAbortError } from './abortSignal';
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly body: unknown;
  readonly endpoint: string;
  readonly method: string;

  constructor(
    status: number,
    message: string,
    options?: {
      body?: unknown;
      statusText?: string;
      endpoint?: string;
      method?: string;
    }
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = options?.statusText?.trim() || '';
    this.body = options?.body ?? null;
    this.endpoint = options?.endpoint?.trim() || '';
    this.method = options?.method?.trim() || 'GET';
  }

  /** Full detail for UI banners and console debugging. */
  displayMessage(): string {
    const statusLabel = this.statusText || `HTTP ${this.status}`;
    const base = this.message.trim() || statusLabel;
    if (base.includes(String(this.status))) {
      return base;
    }
    return `${statusLabel} (${this.status}) — ${base}`;
  }

  /** JSON-safe snapshot for devtools logging. */
  toDebugRecord(): Record<string, unknown> {
    return redactSensitiveRecord({
      status: this.status,
      statusText: this.statusText || undefined,
      method: this.method,
      endpoint: this.endpoint || undefined,
      message: this.message,
      body: this.body ?? '(empty response body)',
    }) as Record<string, unknown>;
  }
}

function tryParseJson(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}

/** Parse any HTTP response body — JSON when possible, even without content-type. */
export async function parseApiResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  const text = await response.text();
  if (!text.trim()) {
    return null;
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json') || contentType.includes('+json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return tryParseJson(text);
}

function readNestedMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => readNestedMessage(entry))
      .filter((entry): entry is string => Boolean(entry));
    return parts.length ? parts.join('; ') : null;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const key of ['message', 'error', 'detail', 'title', 'reason', 'description']) {
      const nested = readNestedMessage(record[key]);
      if (nested) return nested;
    }
  }
  return null;
}

/** Pull a human-readable message from an API error response body. */
export function extractApiErrorMessage(
  status: number,
  data: unknown,
  statusText = ''
): string {
  const fromBody = readNestedMessage(data);
  if (fromBody) {
    return fromBody;
  }

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record.errors) && record.errors.length > 0) {
      const joined = record.errors
        .map((entry) => {
          if (typeof entry === 'string') return entry;
          if (entry && typeof entry === 'object') {
            const item = entry as Record<string, unknown>;
            const field = typeof item.field === 'string' ? item.field : null;
            const msg = readNestedMessage(item.message ?? item.error ?? item.detail);
            if (field && msg) return `${field}: ${msg}`;
            return msg;
          }
          return String(entry);
        })
        .filter(Boolean)
        .join('; ');
      if (joined) return joined;
    }
  }

  const label = statusText.trim();
  switch (status) {
    case 400:
      return label || 'Invalid request — check required fields and formats.';
    case 401:
      return label || 'Not signed in or your session expired. Sign in again.';
    case 403:
      return label || 'Forbidden — you may lack permission for this action.';
    case 404:
      return label || 'Not found — the resource may have been removed.';
    case 409:
      return label || 'Conflict — duplicate record or invalid state.';
    case 422:
      return label || 'Invalid data — check all required fields and formats.';
    case 429:
      return (
        label ||
        'Too many requests — wait a moment and try again. This is a shared rate limit, not a module permission error.'
      );
    case 502:
      return label || 'School API unreachable — backend or proxy is down.';
    case 504:
      return label || 'School API timed out — backend did not respond in time.';
    default:
      return label || `Request failed (HTTP ${status})`;
  }
}

/** Format a thrown API error for display in StatusBanner / toasts. */
export function formatApiError(err: unknown, fallback = 'Request failed'): string {
  if (isAbortError(err)) {
    return '';
  }
  if (err instanceof ApiError) {
    return err.displayMessage();
  }
  if (err instanceof Error) {
    const msg = err.message.trim();
    if (msg) return msg;
  }
  return fallback;
}

export function logApiError(err: unknown, context?: { url?: string; method?: string }): void {
  if (isAbortError(err)) return;
  if (err instanceof ApiError) {
    console.error('[API error]', {
      ...err.toDebugRecord(),
      url: context?.url,
    });
    return;
  }
  console.error('[API error]', context, err);
}
